import React, { useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { PerformanceFiltersState, PerformanceSortKey } from '../../types';
import { useFinancePerformanceData } from '../../hooks/useFinancePerformanceData';
import { PerformanceFilters, PerformanceKpis, PerformanceTable } from '../components';

const DEFAULT_FILTERS: PerformanceFiltersState = {
  groupBy: 'SALES_REP',
  dateRange: '90',
  dealStatus: 'PIPELINE',
  paymentType: 'ALL',
};

export const FinancePerformancePage = () => {
  const [filters, setFilters] = useState<PerformanceFiltersState>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<PerformanceSortKey>('totalProfit');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { rows: rawRows, loading } = useFinancePerformanceData(filters);

  const rows = [...rawRows].sort((a, b) => {
    const av = a[sortKey] as number | string;
    const bv = b[sortKey] as number | string;
    if (typeof av === 'string')
      return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
    return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
  });

  const handleSort = (key: PerformanceSortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <PerformanceFilters filters={filters} onChange={setFilters} />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <PerformanceKpis rows={rows} />
          <PerformanceTable rows={rows} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
        </>
      )}
      <Stack direction="row" spacing={2} mt={1.5} flexWrap="wrap">
        {[
          ['Closed won', 'terminal won status (Funded)'],
          ['Pipeline', 'all open, excl. Dead/Lost/Cancelled'],
          ['Financing', 'payment type finance'],
          ['Cash', 'cash buyer'],
          ['Avg / deal (all)', 'profit / total deals'],
          ['Avg / deal (F&I)', 'profit / non-zero revenue deals'],
        ].map(([label, desc]) => (
          <Typography key={label} variant="caption" color="text.disabled">
            <strong style={{ color: '#888' }}>{label}</strong> = {desc}
          </Typography>
        ))}
      </Stack>
      <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
        Rank badges follow active sort column. Penetration bars proportional to column max (green &gt;= 70%, amber
        40-69%, red &lt; 40%).
      </Typography>
    </Box>
  );
};
